import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FaMobileAlt,
  FaTrophy,
  FaBox,
  FaGift,
  FaMedal,
  FaBolt,
  FaBell,
  FaStar,
  FaArrowUp,
  FaChartBar,
  FaRocket,
  FaCalendarAlt,
  FaCheckCircle,
} from 'react-icons/fa';
import { AuthContext } from '../contexts/AuthContext';
import mockupImage from '../assets/images/mockupApp1.1.jpg';
import './RegisterSuccess.css';

const RegisterSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const userType = location.state?.userType || 'client';

  // 🔥 Função para obter o nome do usuário de várias fontes
  const getUserName = () => {
    // 1. Do contexto
    if (user) {
      // Tenta diferentes campos que podem conter o nome
      const name = user.nome || user.name || user.full_name || user.username;
      if (name) return name;
    }

    // 2. Do localStorage (fallback)
    try {
      const storedUser = localStorage.getItem('cargafacil_user');
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        const name = parsed.nome || parsed.name || parsed.full_name || parsed.username;
        if (name) return name;
      }
    } catch (e) {
      console.warn('Erro ao ler usuário do localStorage:', e);
    }

    // 3. Fallback final
    return 'Usuário';
  };

  const userName = getUserName();

  // Data de lançamento
  const launchDate = new Date();
  launchDate.setDate(launchDate.getDate() + 90);

  const calculateTimeLeft = () => {
    const diff = launchDate - new Date();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((diff / 1000 / 60) % 60),
      seconds: Math.floor((diff / 1000) % 60),
    };
  };

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bom dia';
    if (hour < 18) return 'Boa tarde';
    return 'Boa noite';
  };

  const getBenefits = () => {
    if (userType === 'driver') {
      return {
        title: 'Oferta Especial para os Primeiros 50 Motoristas!',
        icon: <FaTrophy className="benefit-icon" />,
        intro:
          'Parabéns, você chegou a tempo! Ao se cadastrar agora, garante benefícios exclusivos que vão impulsionar sua carreira.',
        items: [
          { icon: <FaCalendarAlt />, text: '30 dias de uso gratuito antes do lançamento oficial' },
          { icon: <FaCalendarAlt />, text: '15 dias de uso gratuito após o lançamento (para os primeiros 50)' },
          { icon: <FaStar />, text: 'Badge "Motorista Fundador" no seu perfil' },
          { icon: <FaArrowUp />, text: 'Prioridade na lista de motoristas disponíveis' },
          { icon: <FaChartBar />, text: 'Acesso antecipado a estatísticas e relatórios' },
        ],
        footer:
          'Esses benefícios são cumulativos e garantem que você comece com vantagem competitiva.',
      };
    } else {
      return {
        title: 'Você se tornou um Cliente Embaixador!',
        icon: <FaTrophy className="benefit-icon" />,
        intro:
          'Como um dos primeiros clientes a se cadastrar, você desbloqueou vantagens exclusivas que tornarão sua experiência ainda melhor.',
        items: [
          { icon: <FaBox />, text: '5 solicitações sem taxa de serviço (antes do lançamento)' },
          { icon: <FaGift />, text: 'Desconto especial nas primeiras 10 solicitações após o lançamento' },
          { icon: <FaMedal />, text: 'Badge "Cliente Embaixador" no seu perfil' },
          { icon: <FaBolt />, text: 'Prioridade nas solicitações de frete' },
          { icon: <FaBell />, text: 'Notificações exclusivas sobre novas funcionalidades' },
        ],
        footer:
          'Aproveite ao máximo sua posição de destaque. Você está à frente do mercado!',
      };
    }
  };

  const benefits = getBenefits();

  return (
    <div className="page-container">
      <div className="auth-container success-page">
        <button className="home-button" onClick={() => navigate('/')}>
          ← Voltar para a Home
        </button>

        <div className="mockup-section">
          <img src={mockupImage} alt="Aplicativo Carga Fácil" className="mockup-image" />
        </div>

        <div className="success-header">
          <h1>
            <FaCheckCircle className="check-icon" /> Cadastro realizado com sucesso!
          </h1>
          <p className="greeting">
            {getGreeting()}, <strong>{userName}</strong>!
          </p>
          <p className="sub-greeting">
            Bem-vindo(a) à família Carga Fácil. Você deu o primeiro passo para revolucionar o transporte de cargas em Moçambique.
          </p>
        </div>

        <div className="countdown-section">
          <h3>
            <FaMobileAlt className="countdown-icon" /> O aplicativo móvel chega em:
          </h3>
          <div className="countdown">
            {[
              { label: 'dias', value: timeLeft.days },
              { label: 'horas', value: timeLeft.hours },
              { label: 'min', value: timeLeft.minutes },
              { label: 'seg', value: timeLeft.seconds },
            ].map((item) => (
              <div key={item.label} className="countdown-item">
                <span className="number">{String(item.value).padStart(2, '0')}</span>
                <span className="label">{item.label}</span>
              </div>
            ))}
          </div>
          <p className="countdown-message">
            {timeLeft.days <= 0
              ? 'O lançamento é hoje!'
              : timeLeft.days <= 7
              ? 'Últimos dias! Prepare-se para a revolução do transporte.'
              : 'Fique atento, em breve você poderá usar o app em qualquer lugar.'}
          </p>
        </div>

        <div className="benefits-section">
          <h2>
            {benefits.icon} {benefits.title}
          </h2>
          <p className="benefits-intro">{benefits.intro}</p>
          <ul className="benefits-list">
            {benefits.items.map((item, index) => (
              <li key={index}>
                <span className="item-icon">{item.icon}</span>
                {item.text}
              </li>
            ))}
          </ul>
          <p className="benefits-footer">{benefits.footer}</p>
        </div>

        <div className="action-section">
          <p className="final-message">
            <FaRocket className="rocket-icon" />
            {userType === 'driver'
              ? ' Você está pronto para começar a ganhar com a Carga Fácil. Em breve, você poderá aceitar fretes diretamente pelo app.'
              : ' Você está pronto para enviar suas cargas com mais segurança e agilidade. O app será seu grande aliado.'}
          </p>
          <button className="btn-submit" onClick={() => navigate('/')}>
            Ir para a Home
          </button>
          <p className="helper-text">
            Enquanto isso, explore nosso site e fique por dentro das novidades.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterSuccess;